// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import ButtonWithConfirmation from 'common/components/ButtonWithConfirmation';
import ButtonWithForceConfirmation from 'common/components/ButtonWithForceConfirmation';
import { SERVER } from 'admin/computeResource/constants/tests';
import {
    Badge,
    Button,
    Form,
    Popover,
    Section,
    Text,
    Toolbar,
    ToolbarExpander,
    ToolbarGroup,
    Translate,
} from '@plesk/ui-library';
import { ComputeResourceVmStatusFilter } from 'admin/common/components/Filters/ComputeResourceVmStatusFilter/ComputeResourceVmStatusFilter';
import {
    ComputeResourceFilter,
    IComputeResourceFilterOptionType,
} from 'admin/common/components/Filters/ComputeResourceFilter/ComputeResourceFilter';
import { UserFilter } from 'admin/common/components/Filters/UserFilter/UserFilter';
import { RootState } from 'admin/core/store';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { connect } from 'react-redux';
import { PropsValue } from 'react-select';
import { ISelectRequiredOption } from 'common/components';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as computeResourceVmActions from 'common/modules/computeResourceVm/actions';
import ButtonWithInputConfirmation from 'common/components/ButtonWithInputConfirmation/ButtonWithInputConfirmation';
import { ICONS } from 'common/constants';
import CopyText from 'common/containers/CopyText/CopyText';
import { VirtualizationTypeFilter } from 'admin/common/components/Filters/VirtualizationTypeFilter/VirtualizationTypeFilter';
import { StorageTypeFilter } from 'admin/common/components/Filters/StorageTypeFilter/StorageTypeFilter';
import { hasPermission } from 'common/modules/permission/selectors';
import { PERMISSION_LIST } from 'common/modules/permission/constants';
import { DELETE_CONFIRMATION } from 'admin/common/constants/constants';
import { VerticalFiltersContainer } from 'admin/common/styles/Styles';
import { TEST } from 'admin/computeResourceVm/constants';
import { TagsFilter } from 'admin/common/components/Filters/TagsFilter/TagsFilter';

export interface IComputeResourceVmOperationsProps {
    selection: number[];
    setSelection: (items: string[]) => void;
    withFilters?: boolean;
    openMigrationDialog: () => void;
    handleBatchDelete: () => void;
    selectedVmStatus?: ISelectRequiredOption;
    onSelectedVmStatusFilterChange: (option: PropsValue<ISelectRequiredOption>) => void;
    selectedCr?: IComputeResourceFilterOptionType;
    onSelectedCrFilterChange: (option: PropsValue<IComputeResourceFilterOptionType>) => void;
    selectedUser?: ISelectRequiredOption;
    onSelectedUserFilterChange: (option: PropsValue<ISelectRequiredOption>) => void;
    selectedVirtualizationType?: ISelectRequiredOption;
    onSelectedVirtualizationTypeFilterChange: (option: PropsValue<ISelectRequiredOption>) => void;
    selectedStorageType?: ISelectRequiredOption;
    onSelectedStorageTypeFilterChange: (option: PropsValue<ISelectRequiredOption>) => void;
    selectedTags?: string[];
    onSelectedTagOptionsFilterChange?: (options: Array<PropsValue<ISelectRequiredOption>>) => void;
    filteredCount: number;
    openSyncImagePresetsDialog: () => void;
    openBatchChangeStorageTypeDialog: () => void;
}

export type ComputeResourceVmOperationsProps =
    IComputeResourceVmOperationsProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const ComputeResourceVmOperations: React.FC<ComputeResourceVmOperationsProps> = ({
    selection,
    setSelection,
    selectedVmStatus,
    onSelectedVmStatusFilterChange,
    selectedCr,
    onSelectedCrFilterChange,
    selectedUser,
    onSelectedUserFilterChange,
    selectedVirtualizationType,
    onSelectedVirtualizationTypeFilterChange,
    selectedStorageType,
    onSelectedStorageTypeFilterChange,
    selectedTags,
    onSelectedTagOptionsFilterChange,
    filteredCount,
    computeResourceVmActions: {
        startComputeResourceVms,
        restartComputeResourceVms,
        stopComputeResourceVms,
        batchInstallGuestTools,
    },
    withFilters,
    isRunningBatchStart,
    isRunningBatchStop,
    isRunningBatchRestart,
    isRunningBatchDelete,
    isRunningBatchInstallGuestTools,
    isRunningBatchSyncImagePresets,
    canBatchStart,
    canBatchStop,
    canBatchPowerOff,
    canBatchRestart,
    canBatchDelete,
    openMigrationDialog,
    handleBatchDelete,
    openSyncImagePresetsDialog,
    openBatchChangeStorageTypeDialog,
    canChangeStorageType,
    isRunningBatchChangeStorageType,
}) => {
    const isBatchOperationsDisabled = isRunningBatchStart
        || isRunningBatchStop
        || isRunningBatchRestart
        || isRunningBatchDelete
        || isRunningBatchInstallGuestTools
        || isRunningBatchSyncImagePresets
        || isRunningBatchChangeStorageType
        || selection.length === 0;
    const handleBatchRestart = (force?: boolean) => {
        restartComputeResourceVms(selection, { force: force || false });
        setSelection([]);
    };

    const handleBatchStart = () => {
        startComputeResourceVms(selection);
        setSelection([]);
    };

    const handleBatchStop = (force?: boolean) => {
        stopComputeResourceVms(selection, { force: force || false });
        setSelection([]);
    };

    const handleBatchPowerOff = () => {
        stopComputeResourceVms(selection, { force: true });
        setSelection([]);
    };

    const handleBatchInstallGuestTools = () => {
        batchInstallGuestTools(selection);
        setSelection([]);
    };

    return (
        <Toolbar>
            {canBatchRestart && (
                <ToolbarGroup title="restart-action">
                    <ButtonWithForceConfirmation
                        data-cy={SERVER.BATCH_ACTIONS.RESTART}
                        disabled={isBatchOperationsDisabled}
                        isLoading={isRunningBatchRestart}
                        confirmationButtonGhost={false}
                        confirmationButtonText={<Translate content="computeResource.servers.batchActions.restart" />}
                        translations={{
                            title: (
                                <Translate content="computeResource.servers.batchActions.batchRestartPopover.title" />
                            ),
                            button: (
                                <Translate content="computeResource.servers.batchActions.batchRestartPopover.button" />
                            ),
                            tooltip: (
                                <Translate content="computeResource.servers.batchActions.batchRestartPopover.tooltip" />
                            ),
                            forceLabel: (
                                <Translate content="computeResource.servers.restartPopover.forceLabel" />
                            ),
                            forceWarning: (
                                <Translate content="computeResource.servers.restartPopover.forceWarning" />
                            ),
                        }}
                        icon={ICONS.RESET}
                        handleConfirm={handleBatchRestart}
                        withForceCheckbox={true}
                    />
                </ToolbarGroup>
            )}
            {canBatchStart && (
                <ToolbarGroup title="start-action">
                    <ButtonWithConfirmation
                        data-cy={SERVER.BATCH_ACTIONS.START}
                        disabled={isBatchOperationsDisabled}
                        isLoading={isRunningBatchStart}
                        confirmationButtonGhost={false}
                        confirmationButtonText={<Translate content="computeResource.servers.batchActions.start" />}
                        translations={{
                            title: (
                                <Translate content="computeResource.servers.batchActions.batchStartPopover.title" />
                            ),
                            button: (
                                <Translate content="computeResource.servers.batchActions.batchStartPopover.button" />
                            ),
                            tooltip: (
                                <Translate content="computeResource.servers.batchActions.batchStartPopover.tooltip" />
                            ),
                        }}
                        icon={ICONS.START_CIRCLE}
                        handleConfirm={handleBatchStart}
                    />
                </ToolbarGroup>
            )}
            {canBatchStop && (
                <ToolbarGroup title="stop-action">
                    <ButtonWithForceConfirmation
                        data-cy={SERVER.BATCH_ACTIONS.STOP}
                        disabled={isBatchOperationsDisabled}
                        isLoading={isRunningBatchStop}
                        confirmationButtonGhost={false}
                        confirmationButtonText={<Translate content="computeResource.servers.batchActions.stop" />}
                        translations={{
                            title: (
                                <Translate content="computeResource.servers.batchActions.batchStopPopover.title" />
                            ),
                            button: (
                                <Translate content="computeResource.servers.batchActions.batchStopPopover.button" />
                            ),
                            tooltip: (
                                <Translate content="computeResource.servers.batchActions.batchStopPopover.tooltip" />
                            ),
                            forceLabel: (
                                <Translate content="computeResource.servers.stopPopover.forceLabel" />
                            ),
                            forceWarning: (
                                <Translate content="computeResource.servers.stopPopover.forceWarning" />
                            ),
                        }}
                        icon="stop-circle"
                        handleConfirm={handleBatchStop}
                        withForceCheckbox={true}
                    />
                </ToolbarGroup>
            )}
            {canBatchPowerOff && (
                <ToolbarGroup title="power-off-action">
                    <ButtonWithConfirmation
                        data-cy={SERVER.BATCH_ACTIONS.POWER_OFF}
                        disabled={isBatchOperationsDisabled}
                        isLoading={isRunningBatchStop}
                        confirmationButtonGhost={false}
                        confirmationButtonText={<Translate content="computeResource.servers.batchActions.powerOff" />}
                        translations={{
                            title: (
                                <Translate content="computeResource.servers.batchActions.batchPowerOffPopover.title" />
                            ),
                            button: (
                                <Translate content="computeResource.servers.batchActions.batchPowerOffPopover.button" />
                            ),
                            text: (
                                <Translate content="computeResource.servers.batchActions.batchPowerOffPopover.text" />
                            ),
                            tooltip: (
                                <Translate content="computeResource.servers.batchActions.batchPowerOffPopover.tooltip" />
                            ),
                        }}
                        icon={ICONS.POWER}
                        handleConfirm={handleBatchPowerOff}
                    />
                </ToolbarGroup>
            )}
            <ToolbarGroup title="migrate-action">
                <Button
                    data-cy={SERVER.BATCH_ACTIONS.MIGRATE}
                    disabled={isBatchOperationsDisabled}
                    ghost={false}
                    icon={ICONS.TRANSFER}
                    onClick={openMigrationDialog}
                >
                    <Translate content="computeResource.servers.batchActions.migrate" />
                </Button>
            </ToolbarGroup>
            <ToolbarGroup title="remove-action">
                <ButtonWithInputConfirmation
                    data-cy={SERVER.BATCH_ACTIONS.REMOVE}
                    disabled={isBatchOperationsDisabled || !canBatchDelete}
                    isLoading={isRunningBatchDelete}
                    ghost={false}
                    confirmation={DELETE_CONFIRMATION}
                    placement={'bottom'}
                    translations={{
                        title: (
                            <Translate content="computeResource.servers.batchActions.deleteConfirmation.title" />
                        ),
                        label: (
                            <Translate
                                content="computeResource.servers.batchActions.deleteConfirmation.label"
                                params={{ confirmation: <CopyText isInline={true}>{DELETE_CONFIRMATION}</CopyText> }}
                            />
                        ),
                        text: (
                            <Translate
                                content="computeResource.servers.batchActions.deleteConfirmation.text"
                                params={{ count: <Text bold>{selection.length}</Text> }}
                            />
                        ),
                        button: (
                            <Translate content="computeResource.servers.batchActions.delete" />
                        ),
                        confirmationButton: (
                            <Translate content="computeResource.servers.batchActions.batchDeletePopover.button" />
                        ),
                        tooltip: (canBatchDelete
                            ? <Translate content="computeResource.servers.batchActions.batchDeletePopover.tooltip" />
                            : <Translate content="servers.delete.cannotDelete" />
                        ),
                    }}
                    icon={ICONS.RECYCLE}
                    handleConfirm={handleBatchDelete}
                />
            </ToolbarGroup>
            <ToolbarGroup title="install-guest-tools-action">
                <ButtonWithConfirmation
                    data-cy={SERVER.BATCH_ACTIONS.INSTALL_GUEST_TOOLS}
                    disabled={isBatchOperationsDisabled}
                    isLoading={isRunningBatchInstallGuestTools}
                    confirmationButtonGhost={false}
                    confirmationButtonText={<Translate content="computeResource.servers.batchActions.installGuestTools.button" />}
                    translations={{
                        title: (
                            <Translate content="computeResource.servers.batchActions.installGuestTools.confirmation.title" />
                        ),
                        button: (
                            <Translate content="computeResource.servers.batchActions.installGuestTools.confirmation.button" />
                        ),
                        tooltip: (
                            <Translate content="computeResource.servers.batchActions.installGuestTools.tooltip" />
                        ),
                    }}
                    icon={ICONS.TOOLS}
                    handleConfirm={handleBatchInstallGuestTools}
                />
            </ToolbarGroup>
            <ToolbarGroup title="sync-image-presets-action">
                <Button
                    data-cy={SERVER.BATCH_ACTIONS.SYNC_IMAGE_PRESETS}
                    disabled={isBatchOperationsDisabled}
                    ghost={false}
                    icon={ICONS.ARROWS_LOOP}
                    onClick={openSyncImagePresetsDialog}
                >
                    <Translate content="computeResource.servers.batchActions.syncImagePresets" />
                </Button>
            </ToolbarGroup>
            <ToolbarGroup title="batch-change-storage-type">
                <Button
                    data-cy={SERVER.BATCH_ACTIONS.CHANGE_STORAGE_TYPE}
                    disabled={isBatchOperationsDisabled || !canChangeStorageType}
                    ghost={false}
                    icon={ICONS.STORAGE}
                    onClick={openBatchChangeStorageTypeDialog}
                >
                    <Translate content="computeResource.servers.batchActions.changeStorageType" />
                </Button>
            </ToolbarGroup>
            {withFilters && (
                <>
                    <ToolbarExpander />

                    <Popover
                        canCloseOnOutsideClick={false}
                        target={(
                            <Badge label={filteredCount} hidden={!filteredCount}>
                                <Button
                                    icon={ICONS.FILTER}
                                    data-cy={TEST.ACTIONS.FILTERS}
                                >
                                    <Translate content="computeResource.servers.filters" />
                                </Button>
                            </Badge>
                        )}
                        placement="left-bottom"
                    >
                        <Section title={<Translate content="computeResource.servers.filters" />}>
                            <Form
                                submitButton={false}
                                cancelButton={false}
                                applyButton={false}
                                vertical={true}
                                footerClassName="hidden"
                            >
                                <VerticalFiltersContainer>
                                    <ComputeResourceVmStatusFilter
                                        onChange={onSelectedVmStatusFilterChange}
                                        value={selectedVmStatus}
                                    />
                                    <ComputeResourceFilter
                                        onChange={onSelectedCrFilterChange}
                                        value={selectedCr}
                                    />
                                    <UserFilter
                                        onChange={onSelectedUserFilterChange}
                                        value={selectedUser}
                                    />
                                    <VirtualizationTypeFilter
                                        onChange={onSelectedVirtualizationTypeFilterChange}
                                        value={selectedVirtualizationType}
                                    />
                                    <StorageTypeFilter
                                        onChange={onSelectedStorageTypeFilterChange}
                                        value={selectedStorageType}
                                    />
                                    <TagsFilter
                                        onChange={onSelectedTagOptionsFilterChange}
                                        value={selectedTags?.map(item => ({ value: item, label: item }))}
                                    />
                                </VerticalFiltersContainer>
                            </Form>
                        </Section>
                    </Popover>
                </>
            )}
        </Toolbar>
    );
};


const mapStateToProps = (state: RootState) => ({
    isRunningBatchStart: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_VM_BATCH_START),
    isRunningBatchStop: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_VM_BATCH_STOP),
    isRunningBatchRestart: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_VM_BATCH_RESTART),
    isRunningBatchDelete: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_VM_BATCH_DELETE),
    isRunningBatchInstallGuestTools: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_VM_BATCH_INSTALL_GUEST_TOOLS),
    isRunningBatchSyncImagePresets: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_VM_SYNC_IMAGE_PRESETS),
    isRunningBatchChangeStorageType: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_VM_CHANGE_STORAGE_TYPE),
    canBatchStart: hasPermission(state, PERMISSION_LIST.START_SERVERS, PERMISSION_LIST.MANAGE_SERVERS),
    canBatchStop: hasPermission(state, PERMISSION_LIST.STOP_SERVERS, PERMISSION_LIST.MANAGE_SERVERS),
    canBatchPowerOff: hasPermission(state, PERMISSION_LIST.POWER_OFF_SERVERS, PERMISSION_LIST.MANAGE_SERVERS),
    canBatchRestart: hasPermission(state, PERMISSION_LIST.RESTART_SERVERS, PERMISSION_LIST.MANAGE_SERVERS),
    canBatchDelete: hasPermission(state, PERMISSION_LIST.DELETE_SERVERS),
    canSyncImagePresets: hasPermission(state, PERMISSION_LIST.MANAGE_SERVERS, PERMISSION_LIST.SYNC_IMAGE_PRESETS),
    canChangeStorageType: hasPermission(state, PERMISSION_LIST.MANAGE_SERVERS, PERMISSION_LIST.CHANGE_STORAGES_FOR_SERVERS),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    computeResourceVmActions: bindActionCreators(computeResourceVmActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ComputeResourceVmOperations);
